import { Controller } from "stimulus"
import SlimSelect from "slim-select"

// Turns the opportunity's event field into a type-ahead SlimSelect and
// narrows the available events to those whose category matches one of the
// categories currently checked on the opportunity (or, on a form whose
// categories are fixed rather than chosen, one of the categories it names in
// data-event-picker-category-ids). Events with no category
// are always offered (they are not tied to any category), and the event
// already saved on the opportunity is never filtered out so editing can
// never silently drop it.
//
// The full option list is read from the data-event-picker-events JSON
// attribute rather than the select's <option> elements: SlimSelect v1's
// setData() rebuilds the native options (dropping filtered-out entries and
// their data attributes), so the DOM is not a reliable source after init —
// especially on Turbo cache restores.
export default class extends Controller {
  static targets = ["category", "select"]

  connect() {
    // The event select is only rendered when there are events to offer, but
    // the controller is attached to the form unconditionally.
    if (!this.hasSelectTarget) return

    // Read the blank option's wording before SlimSelect rebuilds the native
    // options: from then on the blank is an empty-text placeholder and the
    // label is no longer recoverable from the DOM.
    this.blankLabel = this.readBlankLabel()

    this.options = JSON.parse(this.data.get("events") || "[]").map((event) => ({
      value: String(event.id),
      text: event.label,
      categoryId: event.category_id
    }))

    // A Turbo cache snapshot may already contain SlimSelect markup from a
    // previous visit (the snapshot is taken before disconnect runs). Remove
    // any stray widgets so re-initializing doesn't render the select twice.
    this.removeStrayWidgets()

    // The blank's wording lives in the widget's placeholder rather than in a
    // dropdown row, so the closed widget still reads "-- No event --" when
    // nothing is chosen. A placeholder row is hidden from the dropdown, so
    // allowDeselect supplies the clear control in its place — see
    // makeDeselectKeyboardOperable for the affordances it needs to be usable.
    this.slimSelect = new SlimSelect({
      select: this.selectTarget,
      addToBody: true,
      placeholder: this.blankLabel,
      allowDeselect: true
    })

    this.makeDeselectKeyboardOperable()

    this.applyFilter()

    // Tear down before Turbo snapshots the page so the cached DOM holds the
    // pristine native select instead of the generated SlimSelect widget.
    this.beforeCache = () => this.teardown()
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.teardown()
  }

  teardown() {
    if (this.slimSelect) {
      try {
        this.slimSelect.destroy()
      } catch (error) {
        // SlimSelect DOM may already be gone on Turbo teardown.
      }
      this.slimSelect = null
    }
    this.removeStrayWidgets()
  }

  // SlimSelect v1 renders the deselect control as a bare <span> carrying only
  // a click handler (slimselect.js:1362-1372), and it hides the placeholder
  // row from the dropdown (:1852-1858). The "x" is therefore the only way to
  // clear a chosen event, and as the library ships it a keyboard-only user
  // cannot reach it — so give it the affordances the library omits. The node
  // is built once in SlimSelect's constructor and only shown and hidden after
  // that, so setting this up here survives every later setData().
  makeDeselectKeyboardOperable() {
    const deselect = this.deselectElement()
    if (!deselect) return

    deselect.setAttribute("tabindex", "0")
    deselect.setAttribute("role", "button")
    deselect.setAttribute("aria-label", "Clear selected event")

    deselect.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return

      // Space would otherwise scroll, and the keystroke would reach the
      // widget container and reopen the dropdown we just cleared.
      event.preventDefault()
      event.stopPropagation()
      this.slimSelect.set("")
    })
  }

  deselectElement() {
    const parent = this.selectTarget.parentNode
    return parent ? parent.querySelector(".ss-main .ss-deselect") : null
  }

  removeStrayWidgets() {
    if (!this.hasSelectTarget) return

    const parent = this.selectTarget.parentNode
    if (!parent) return

    parent.querySelectorAll(".ss-main").forEach((widget) => widget.remove())
    this.selectTarget.style.display = ""
  }

  filter() {
    this.applyFilter()
  }

  applyFilter() {
    if (!this.slimSelect) return

    const selectedCategoryIds = this.selectedCategoryIds()

    const currentValue = this.slimSelect.selected()

    // The blank must be a placeholder entry, not an ordinary empty-valued
    // one: SlimSelect v1 promotes an empty value to the entry's own text
    // (slimselect.js:739-740, :1263), so a blank labelled "-- No event --"
    // would have the form post that label, which Rails casts to event id 0
    // and Postgres rejects against the events table. A placeholder keeps its
    // empty value, so "no event" posts as blank and saves as nil.
    const data = [{ value: "", text: "", placeholder: true }].concat(
      this.options
        .filter((option) => this.optionVisible(option, selectedCategoryIds, currentValue))
        .map((option) => ({
          value: option.value,
          text: option.text,
          selected: option.value === currentValue
        }))
    )

    this.slimSelect.setData(data)
  }

  // The categories events are narrowed to: those checked on the form, or, on
  // a form with no category checkboxes because its categories are fixed (New
  // Opportunity from template copies the template's), the ones it names in
  // data-event-picker-category-ids.
  selectedCategoryIds() {
    if (!this.hasCategoryTarget) return JSON.parse(this.data.get("categoryIds") || "[]")

    return this.categoryTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => parseInt(checkbox.value, 10))
      .filter((id) => !Number.isNaN(id))
  }

  readBlankLabel() {
    const blank = Array.from(this.selectTarget.options).find((option) => option.value === "")
    return blank ? blank.text : "-- No event --"
  }

  optionVisible(option, selectedCategoryIds, currentValue) {
    if (option.value === currentValue) return true
    if (selectedCategoryIds.length === 0) return true
    if (option.categoryId === null) return true
    return selectedCategoryIds.includes(option.categoryId)
  }
}
